import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FileVideo, Upload, Bot, Trash2, Plus, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fakeAiCaption } from '@/utils/mockData';
import { useToast } from '@/hooks/use-toast';

interface VideoData {
  id: string;
  file: File;
  title: string;
  thumbnailUrl?: string;
  duration?: string;
}

interface DialogVideosProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (videos: VideoData[]) => void;
}

export const DialogVideos: React.FC<DialogVideosProps> = ({
  open,
  onOpenChange,
  onSave
}) => {
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [processingAi, setProcessingAi] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  const validateFile = (file: File): boolean => {
    if (!file.type.startsWith('video/')) {
      toast({
        title: "Erro",
        description: "Apenas arquivos de vídeo são permitidos.",
        variant: "destructive"
      });
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "Arquivo muito grande",
        description: `O arquivo ${file.name} excede o limite de 100MB.`,
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const generateThumbnail = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        video.currentTime = Math.min(1, video.duration / 2); // Thumbnail at 1s or middle
      };

      video.onseeked = () => {
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        }
        URL.revokeObjectURL(video.src);
      };

      video.onerror = () => {
        resolve(''); // Return empty string if thumbnail generation fails
        URL.revokeObjectURL(video.src);
      };

      video.src = URL.createObjectURL(file);
      video.load();
    });
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (validateFile(files[i])) {
        validFiles.push(files[i]);
      }
    }

    const newVideos: VideoData[] = [];
    
    for (const file of validFiles) {
      const thumbnailUrl = await generateThumbnail(file);
      
      // Get duration
      const video = document.createElement('video');
      const duration = await new Promise<string>((resolve) => {
        video.onloadedmetadata = () => {
          resolve(formatDuration(video.duration));
          URL.revokeObjectURL(video.src);
        };
        video.onerror = () => {
          resolve('');
          URL.revokeObjectURL(video.src);
        };
        video.src = URL.createObjectURL(file);
      });

      newVideos.push({
        id: `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        file,
        title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
        thumbnailUrl,
        duration
      });
    }

    setVideos(prev => [...prev, ...newVideos]);
  };

  const handleTitleChange = (videoId: string, title: string) => {
    setVideos(prev => prev.map(video => 
      video.id === videoId ? { ...video, title } : video
    ));
  };

  const handleRemoveVideo = (videoId: string) => {
    setVideos(prev => {
      const video = prev.find(v => v.id === videoId);
      if (video && video.thumbnailUrl) {
        // Note: We don't need to revoke thumbnailUrl as it's from canvas.toDataURL
      }
      return prev.filter(v => v.id !== videoId);
    });
  };

  const handleAiCaption = async (videoId: string) => {
    setProcessingAi(videoId);
    const video = videos.find(v => v.id === videoId);
    if (!video) return;

    try {
      const aiTitle = await fakeAiCaption(video.file);
      handleTitleChange(videoId, aiTitle);
      toast({
        title: "Título gerado",
        description: "Título automático gerado com sucesso!"
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao gerar título automático.",
        variant: "destructive"
      });
    } finally {
      setProcessingAi(null);
    }
  };

  const handleAiCorrect = async (videoId: string) => {
    setProcessingAi(videoId);
    const video = videos.find(v => v.id === videoId);
    if (!video || !video.title) return;

    try {
      const correctedTitle = await fakeAiCaption(video.title);
      handleTitleChange(videoId, correctedTitle);
      toast({
        title: "Ortografia corrigida",
        description: "Título corrigido com sucesso!"
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao corrigir ortografia.",
        variant: "destructive"
      });
    } finally {
      setProcessingAi(null);
    }
  };

  const handleSave = () => {
    onSave(videos);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setVideos([]);
    onOpenChange(false);
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adicionar Vídeos</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {videos.length === 0 ? (
            <div
              className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
              onClick={openFileSelector}
            >
              <FileVideo className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Clique para selecionar vídeos ou arraste arquivos aqui
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Máximo 100MB por vídeo • Formatos: MP4, WebM, MOV, AVI
              </p>
              <Button variant="outline" size="sm">
                <Upload className="w-4 h-4 mr-2" />
                Selecionar Vídeos
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {videos.map((video) => (
                  <div key={video.id} className="border rounded-lg p-4 space-y-3">
                    <div className="relative bg-muted rounded-md h-48 flex items-center justify-center">
                      {video.thumbnailUrl ? (
                        <img
                          src={video.thumbnailUrl}
                          alt="Video thumbnail"
                          className="w-full h-full object-cover rounded-md"
                        />
                      ) : (
                        <FileVideo className="w-16 h-16 text-muted-foreground" />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-black/50 rounded-full p-2">
                          <Play className="w-6 h-6 text-white fill-white" />
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 h-8 w-8 p-0"
                        onClick={() => handleRemoveVideo(video.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {video.duration && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          {video.duration}
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={`title-${video.id}`}>Título do Vídeo</Label>
                      <div className="flex gap-2">
                        <Input
                          id={`title-${video.id}`}
                          value={video.title}
                          onChange={(e) => handleTitleChange(video.id, e.target.value)}
                          placeholder="Digite o título do vídeo..."
                          className="flex-1"
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={processingAi === video.id}
                              className="px-3"
                            >
                              <Bot className={cn(
                                "h-4 w-4",
                                processingAi === video.id && "animate-spin"
                              )} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleAiCaption(video.id)}>
                              Gerar título automático
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleAiCorrect(video.id)}
                              disabled={!video.title}
                            >
                              Corrigir ortografia
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {video.file.name} • {(video.file.size / 1024 / 1024).toFixed(2)}MB
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={openFileSelector}
                  className="w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar mais vídeos
                </Button>
              </div>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="video/*"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={videos.length === 0}>
            Salvar ({videos.length} vídeo{videos.length !== 1 ? 's' : ''})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};